// ─────────────────────────────────────────────────────────────────────────────
// analyze_dashboard — proactive insight engine
//
// Walks every registered widget and emits a structured digest:
//   • per-widget: trend direction, magnitude, latest value, anomaly flags
//   • cross-widget: data gaps, concentration risks, framework coverage
//   • aggregates: counts by kind / framework / chart library
//
// This is a one-shot "what's interesting" pass. Claude reads the digest and
// then drills in with read_chart_data / forecast_series for the items that
// matter to the user's question.
// ─────────────────────────────────────────────────────────────────────────────

import { widgetRegistry } from "../registry/widgetRegistry";
import type { RegisteredWidget, WidgetSeries } from "../registry/types";

export type SignalType =
	| "trend_up"
	| "trend_down"
	| "stable"
	| "outlier"
	| "missing_data"
	| "concentration"
	| "kpi_off_target"
	| "framework_gap"
	| "data_quality";

export interface DashboardSignal {
	type: SignalType;
	severity: "info" | "warn" | "critical";
	widgetId?: string;
	widgetTitle?: string;
	description: string;
	metric?: string;
	value?: number | string;
	delta?: number;
}

export interface DashboardSummary {
	totalWidgets: number;
	byKind: Record<string, number>;
	byChartLibrary: Record<string, number>;
	frameworksDetected: string[];
	widgetsWithData: number;
	widgetsWithoutData: number;
}

export interface AnalyzeDashboardInput {
	focus?: string;
	include_widgets_without_data?: boolean;
}

export interface AnalyzeDashboardOutput {
	summary: DashboardSummary;
	signals: DashboardSignal[];
	per_widget: Array<{
		widgetId: string;
		title: string;
		kind: string;
		hasData: boolean;
		latestValue?: number;
		earliestValue?: number;
		changePct?: number;
		trend?: "increasing" | "decreasing" | "stable";
		minValue?: number;
		maxValue?: number;
		framework?: string;
	}>;
	message?: string;
}

// ── Analysis helpers ─────────────────────────────────────────────────────────

function getNumericPoints(series: WidgetSeries): number[] {
	return series.data
		.map((p) => (typeof p.y === "number" ? p.y : Number(p.y)))
		.filter((y) => Number.isFinite(y));
}

function pickPrimarySeries(widget: RegisteredWidget): WidgetSeries | undefined {
	if (!widget.series || widget.series.length === 0) return undefined;
	for (const s of widget.series) {
		if (getNumericPoints(s).length >= 2) return s;
	}
	return widget.series[0];
}

function detectOutliers(values: number[]): number[] {
	if (values.length < 4) return [];
	const m = values.reduce((a, b) => a + b, 0) / values.length;
	const variance =
		values.reduce((s, v) => s + (v - m) ** 2, 0) / values.length;
	const sd = Math.sqrt(variance);
	if (sd === 0) return [];
	return values
		.map((v, i) => ({ v, i, z: (v - m) / sd }))
		.filter((p) => Math.abs(p.z) > 2.5)
		.map((p) => p.i);
}

function widgetHasData(w: RegisteredWidget): boolean {
	if (w.series && w.series.length > 0) {
		return w.series.some((s) => getNumericPoints(s).length > 0);
	}
	if (w.table && w.table.rows && w.table.rows.length > 0) return true;
	if (w.totals && Object.keys(w.totals).length > 0) return true;
	if (w.kpi && (w.kpi.value !== undefined || w.kpi.target !== undefined)) return true;
	if (w.text && w.text.trim().length > 0) return true;
	if (w.geo && w.geo.length > 0) return true;
	return false;
}

// ── Main entry point ─────────────────────────────────────────────────────────

export function analyzeDashboard(input: AnalyzeDashboardInput = {}): AnalyzeDashboardOutput {
	const widgets = widgetRegistry.list();

	if (widgets.length === 0) {
		return {
			summary: {
				totalWidgets: 0,
				byKind: {},
				byChartLibrary: {},
				frameworksDetected: [],
				widgetsWithData: 0,
				widgetsWithoutData: 0,
			},
			signals: [],
			per_widget: [],
			message:
				"No widgets are currently registered. The dashboard may still be loading — try again in a few seconds, or refresh the page context.",
		};
	}

	const byKind: Record<string, number> = {};
	const byChartLibrary: Record<string, number> = {};
	const frameworksSet = new Set<string>();
	const signals: DashboardSignal[] = [];
	const perWidget: AnalyzeDashboardOutput["per_widget"] = [];

	let withData = 0;
	let withoutData = 0;

	for (const w of widgets) {
		byKind[w.kind] = (byKind[w.kind] ?? 0) + 1;
		byChartLibrary[w.chartLibrary] = (byChartLibrary[w.chartLibrary] ?? 0) + 1;
		if (w.framework?.id) frameworksSet.add(w.framework.id);

		const hasData = widgetHasData(w);
		if (hasData) withData++;
		else withoutData++;

		const entry: AnalyzeDashboardOutput["per_widget"][number] = {
			widgetId: w.widgetId,
			title: w.title,
			kind: w.kind,
			hasData,
			framework: w.framework?.id,
		};

		// ── Series-based analysis (charts) ────────────────────────────────
		const series = pickPrimarySeries(w);
		if (series) {
			const ys = getNumericPoints(series);
			if (ys.length >= 2) {
				const first = ys[0];
				const last = ys[ys.length - 1];
				const min = Math.min(...ys);
				const max = Math.max(...ys);

				entry.earliestValue = first;
				entry.latestValue = last;
				entry.minValue = min;
				entry.maxValue = max;

				if (first !== 0) {
					const changePct = ((last - first) / Math.abs(first)) * 100;
					entry.changePct = Math.round(changePct * 100) / 100;
					if (Math.abs(changePct) < 2) {
						entry.trend = "stable";
					} else if (changePct > 0) {
						entry.trend = "increasing";
						signals.push({
							type: "trend_up",
							severity: "info",
							widgetId: w.widgetId,
							widgetTitle: w.title,
							description: `${w.title} is up ${changePct.toFixed(1)}% (${first} → ${last}).`,
							value: last,
							delta: entry.changePct,
						});
					} else {
						entry.trend = "decreasing";
						signals.push({
							type: "trend_down",
							severity: "info",
							widgetId: w.widgetId,
							widgetTitle: w.title,
							description: `${w.title} is down ${Math.abs(changePct).toFixed(1)}% (${first} → ${last}).`,
							value: last,
							delta: entry.changePct,
						});
					}
				}

				const outlierIdx = detectOutliers(ys);
				if (outlierIdx.length > 0) {
					const outlierValues = outlierIdx.map((i) => ys[i]).join(", ");
					signals.push({
						type: "outlier",
						severity: "warn",
						widgetId: w.widgetId,
						widgetTitle: w.title,
						description: `${w.title} has ${outlierIdx.length} statistical outlier(s): ${outlierValues} (>2.5σ).`,
					});
				}
			}
		}

		// ── KPI off-target check ─────────────────────────────────────────
		if (w.kpi && typeof w.kpi.value === "number" && typeof w.kpi.target === "number") {
			const v = w.kpi.value;
			const t = w.kpi.target;
			if (t !== 0) {
				const gap = ((v - t) / t) * 100;
				if (Math.abs(gap) > 10) {
					signals.push({
						type: "kpi_off_target",
						severity: gap < -20 ? "critical" : "warn",
						widgetId: w.widgetId,
						widgetTitle: w.title,
						description: `${w.title}: ${v} vs target ${t} (${gap > 0 ? "+" : ""}${gap.toFixed(1)}%).`,
						value: v,
						delta: Math.round(gap * 100) / 100,
					});
				}
			}
		}

		// ── Missing data ─────────────────────────────────────────────────
		if (!hasData) {
			signals.push({
				type: "missing_data",
				severity: "warn",
				widgetId: w.widgetId,
				widgetTitle: w.title,
				description: `${w.title} has no data. Check the underlying indicator or filters.`,
			});
		}

		// ── Concentration risk (donut/pie with one slice >70%) ───────────
		if (w.totals && Object.keys(w.totals).length > 1) {
			const totalSum = Object.values(w.totals).reduce((a, b) => a + b, 0);
			if (totalSum > 0) {
				for (const [k, v] of Object.entries(w.totals)) {
					if (k === "target") continue;
					const share = (v / totalSum) * 100;
					if (share > 70) {
						signals.push({
							type: "concentration",
							severity: "warn",
							widgetId: w.widgetId,
							widgetTitle: w.title,
							description: `${w.title}: "${k}" represents ${share.toFixed(1)}% of total — concentration risk.`,
						});
					}
				}
			}
		}

		perWidget.push(entry);
	}

	// ── Cross-widget signals ──────────────────────────────────────────────
	if (withoutData > 0 && withoutData / widgets.length > 0.3) {
		signals.push({
			type: "data_quality",
			severity: withoutData / widgets.length > 0.5 ? "critical" : "warn",
			description: `${withoutData} of ${widgets.length} widgets returned no data — likely a data-collection or refresh issue.`,
		});
	}

	if (input.focus) {
		const focus = input.focus.toUpperCase().replace(/\s/g, "");
		if (!Array.from(frameworksSet).some((f) => f.replace(/\s/g, "").includes(focus))) {
			signals.push({
				type: "framework_gap",
				severity: "warn",
				description: `No widgets on this dashboard are tagged with ${input.focus}. Coverage gap if reporting under that framework.`,
			});
		}
	}

	return {
		summary: {
			totalWidgets: widgets.length,
			byKind,
			byChartLibrary,
			frameworksDetected: Array.from(frameworksSet),
			widgetsWithData: withData,
			widgetsWithoutData: withoutData,
		},
		signals: signals.sort((a, b) => severityRank(b.severity) - severityRank(a.severity)),
		per_widget: perWidget,
	};
}

function severityRank(s: DashboardSignal["severity"]): number {
	if (s === "critical") return 3;
	if (s === "warn") return 2;
	return 1;
}

// ── Output formatting ────────────────────────────────────────────────────────

export function formatAnalyzeDashboard(out: AnalyzeDashboardOutput): string {
	if (out.message && out.summary.totalWidgets === 0) {
		return `### analyze_dashboard\n${out.message}`;
	}

	const parts: string[] = [];
	parts.push(`### Dashboard digest`);
	const s = out.summary;
	parts.push(`Widgets: ${s.totalWidgets} (${s.widgetsWithData} with data, ${s.widgetsWithoutData} empty)`);
	const kindStr = Object.entries(s.byKind)
		.map(([k, v]) => `${v} ${k}`)
		.join(", ");
	parts.push(`Kinds: ${kindStr}`);
	if (s.frameworksDetected.length > 0) {
		parts.push(`Frameworks tagged: ${s.frameworksDetected.join(", ")}`);
	}

	if (out.signals.length > 0) {
		parts.push(`\n### Signals (${out.signals.length})`);
		const top = out.signals.slice(0, 20);
		for (const sig of top) {
			const badge =
				sig.severity === "critical" ? "🔴" : sig.severity === "warn" ? "🟡" : "🔵";
			const idTag = sig.widgetId ? ` [${sig.widgetId}]` : "";
			parts.push(`- ${badge} **${sig.type}**${idTag}: ${sig.description}`);
		}
		if (out.signals.length > 20) {
			parts.push(`…(+${out.signals.length - 20} more signals)`);
		}
	} else {
		parts.push(`\nNo notable signals — all widgets within normal bounds.`);
	}

	parts.push(`\n### Per-widget digest`);
	parts.push("| Widget | Kind | Latest | Trend | Δ% | Range |");
	parts.push("|---|---|---|---|---|---|");
	for (const w of out.per_widget) {
		const id = w.widgetId.slice(-8);
		const latest = w.latestValue !== undefined ? String(w.latestValue) : "—";
		const trend =
			w.trend === "increasing" ? "↑" : w.trend === "decreasing" ? "↓" : w.trend === "stable" ? "→" : "—";
		const delta = w.changePct !== undefined ? `${w.changePct > 0 ? "+" : ""}${w.changePct}%` : "—";
		const range =
			w.minValue !== undefined && w.maxValue !== undefined
				? `${w.minValue}..${w.maxValue}`
				: "—";
		parts.push(`| ${w.title} \`${id}\` | ${w.kind} | ${latest} | ${trend} | ${delta} | ${range} |`);
	}

	return parts.join("\n");
}
